import { Link } from 'react-router'
import { useShallow } from 'zustand/react/shallow'
import { Button } from '@/components/ui'
import { analyticsConfigured, UNMASK_AREA } from '@/lib/analytics'
import { useConsentStore } from '@/lib/consentStore'
import { t } from '@/lib/i18n'

/**
 * Product analytics choices (docs/analytics.md). It never opens by itself (MG, 2026-10-07: the Privacy Policy that
 * people agree to when they join explains analytics); "Analytics choices" in the footer opens it, small, at the
 * bottom, never blocking the page. Where analytics are on (by default or after a yes) it offers Opt out; where they
 * are off it asks (Decline is as easy as Accept). A browser privacy signal is never asked: it only explains it.
 */
export function AnalyticsConsentBanner() {
  const { decision, reopened, choose, acknowledge, close } = useConsentStore(
    useShallow((s) => ({ decision: s.decision, reopened: s.reopened, choose: s.choose, acknowledge: s.acknowledge, close: s.close })),
  )
  const kind = !analyticsConfigured() || !reopened ? null : decision.reason === 'signal' ? 'signal' : decision.allowed ? 'notice' : 'ask'
  if (!kind) return null
  const text =
    kind === 'signal'
      ? t('Your browser asks sites not to track you, so product analytics stay off in this browser.')
      : kind === 'notice'
        ? t('We use Mixpanel to learn how people use MG AI School: pages, clicks and session replays, with typed text and private areas hidden. You can opt out at any time.')
        : t('Can we use Mixpanel to learn how people use MG AI School? It records pages, clicks and session replays, with typed text and private areas hidden. Nothing is collected unless you accept.')
  return (
    <section
      role="region"
      aria-label={t('Analytics choices')}
      aria-live="polite"
      data-testid="analytics-consent"
      data-kind={kind}
      {...UNMASK_AREA}
      className="fixed inset-x-3 bottom-[calc(4.25rem+env(safe-area-inset-bottom))] z-50 rounded-xl border border-line bg-surface p-4 shadow-lg sm:right-auto sm:max-w-md md:bottom-4 md:left-4"
    >
      <p className="text-body text-text">
        {text}{' '}
        <Link to="/privacy#product-analytics" className="text-link underline underline-offset-2">
          {t('Privacy Policy')}
        </Link>
      </p>
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        {kind === 'signal' ? (
          <Button type="button" variant="primary" size="sm" onClick={close}>
            {t('OK')}
          </Button>
        ) : kind === 'notice' ? (
          <>
            <Button type="button" variant="secondary" size="sm" onClick={() => choose(false, 'banner')}>
              {t('Opt out')}
            </Button>
            <Button type="button" variant="primary" size="sm" onClick={acknowledge}>
              {t('OK')}
            </Button>
          </>
        ) : (
          <>
            <Button type="button" variant="secondary" size="sm" onClick={() => choose(false, 'banner')}>
              {t('Decline')}
            </Button>
            <Button type="button" variant="primary" size="sm" onClick={() => choose(true, 'banner')}>
              {t('Accept')}
            </Button>
          </>
        )}
      </div>
    </section>
  )
}
